import { Localisation } from "./Localisation";

/**
 * This class parses a string which is expected to be a multi-lingual field, in json format, with the following structure
 * {
 *  "en-GB": "Elevator",
 *  "es-ES": "Ascensor",
 *  "de-DE": "Aufzug",
 *  "ja-JP": "...."
 *  }
 *  the parsed keys are then matched to the list of supported languages taken from appContext().language
 *  (this is to support adding/removing languages in the future)
 *  if parsing fails, the value is treated as a simple, non-localised string and used verbatim for every supported language
 *  (this supports legacy nodes whose display name was stored as a plain string rather than a language object)
 */
export class LanguageValuesHelper
{
    private localisation: Localisation;

    constructor(localisation: Localisation)
    {
        this.localisation = localisation;
    }

    private getLanguages()
    {
        const { languages } = this.localisation.languages;
        return languages.filter(lang => lang.code !== this.localisation.terms)
    }

    public default(): ILanguageValues
    {
        const values: ILanguageValues = {};

        for (const language of this.getLanguages())
        {
            values[language.code] = '';
        }

        return values;
    }

    public fromString(value: string): ILanguageValues
    {
        let parsed: Partial<ILanguageValues> = {};
        try
        {
            parsed = JSON.parse(value || '{}') as ILanguageValues ?? {};
        }
        catch
        {
            // Not valid JSON. Legacy nodes store a plain, non-localised string
            // (e.g. "80 Trafalgar") rather than a complex language object, so use
            // the verbatim value for every supported language.
            for (const language of this.getLanguages())
            {
                parsed[language.code] = value;
            }
        }

        const values: ILanguageValues = {};

        for (const language of this.getLanguages())
        {
            values[language.code] = parsed[language.code] ?? '';
        }

        return values;

    }

    public toString(inputValues: ILanguageValues): string
    {
        const values: ILanguageValues = {}

        for (const language of this.getLanguages())
        {
            values[language.code] = inputValues[language.code] ?? ''
        }
        return JSON.stringify(values);
    }

    public getValue(values: ILanguageValues): string
    {
        return values[this.localisation.languages.current] || values[this.localisation.languages.defaultLanguage];
    }
}

export interface ILanguageValues
{
    [key: string]: string;
}
